<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" fullscreen persistent scrollable>
<k-card>
	<!-- <k-card-title class="black white--text py-2" style="border-bottom:1px solid #999">
		<k-icon color="#fff" class="mr-3" style="font-size:30px;">fas fa-cubes</k-icon>
		<b>Satchel Competency-Resource Alignment System (SCRAM)</b> -->
	<k-card-title class="k-rcram-title"
		:style="`background-color:${report_banner_bgd_color}`"
	>
		<img class="k-rcram-logo" 
			:style="report_logo_style"
			:src="report_logo"
		>
		<div class="k-rcram-report-title"
			v-html="report_title"
		></div>

		<k-spacer/>

		<k-btn color="#666" dark fab small accessible-label="Close resource alignment tool" @click="$emit('dialog_cancel')"><k-icon>fas fa-xmark</k-icon></k-btn>

	</k-card-title>
	<k-card-text class="k-scram-job-card" style="font-size:16px; line-height:19px"
		v-if='initialized'
	>

	<div class="mt-3 mb-5 d-flex align-center">
		<h3>Existing SCRAM Jobs:</h3>

		<k-spacer/>

		<div style="width:300px" class="mr-2">
			<k-select dense outlined hide-details append-icon="fas fa-caret-down"
				label="Jobs to Show"
				v-model="job_type_to_show"
				:items="job_type_menu_items"
			></k-select>
		</div>

		<div style="width:300px">
			<k-text-field dense outlined hide-details label="" placeholder="Search"
				prepend-inner-icon="fa fa-search" prepend-decorative clearable clear-icon="fa fa-times-circle"
				v-model="search_terms"
			></k-text-field>
		</div>

		<k-spacer/>

		<k-btn color="primary"
			@click="create_new_job"
		><k-icon small class="mr-2">fas fa-plus</k-icon>New Job</k-btn>
	</div>

	<k-data-table light dense
		:headers="table_headers"
		:items="table_rows"
		:footer-props="footer_options"
		:items-per-page="25"
		:sort-by.sync="sort_by_column"
		:sort-desc.sync="sort_descending"
		class="k-rcram-jobs-table"
	>
		<!-- BODY ROWS -->
		<template v-slot:item="{ item }">
			<tr>
				<td class="k-rcram-manager-id-cell"
				>{{item.job_type_abbrev}}: {{item.cram_job_id}}</td>

				<td class="k-rcram-manager-job-title-cell"
				>{{item.cram_job_title}}</td>

				<td class="k-rcram-manager-creator-cell"
					v-if="columns_to_show.creator_email"
				>{{item.creator_email}}</td>

				<td class="k-rcram-framework-title-cell"
					v-if="columns_to_show.resource_framework_title"
				>{{item.resource_framework_title}}</td>

				<td class="k-rcram-framework-title-cell"
					v-if="columns_to_show.comp_framework_title"
				>{{item.comp_framework_title}}</td>

				<td class="k-rcram-manager-count-cell"
					v-html="item.counts_string"
				>
				</td>

				<td class="k-rcram-manager-status-cell">
					<span
						 v-if="!item.matrix_locked_at"
						 v-html="item.worker_status_string"
					></span>
					<k-tooltip bottom v-if="item.matrix_locked_at"><template v-slot:activator="{on}">
						<k-icon v-on="on" small color="#444" style="margin:-6px 0 0 6px">fas fa-lock</k-icon>
					</template>Locked {{item.locked_string}}</k-tooltip>
				</td>

				<td class="k-rcram-manager-ai-cost-cell"
					v-if="columns_to_show.ai_stats"
				>{{item.ai_stats}}</td>

				<td class="k-rcram-manager-actions-cell">

					<v-tooltip bottom><template v-slot:activator="{on}">
						<k-btn v-on="on" small icon color="primary"
							@click="edit_job(item.cram_job_id)"
						><k-icon small>fas fa-eye</k-icon></k-btn>
					</template>View/Edit</v-tooltip>

					<v-tooltip bottom><template v-slot:activator="{on}">
						<k-btn v-on="on" small icon color="primary"
							v-visible="item.reportable"
							@click="view_grid(item.cram_job_id)"
						><k-icon small>fas fa-grid</k-icon></k-btn>
					</template>Grid View</v-tooltip>
					
					<v-tooltip bottom><template v-slot:activator="{on}">
						<k-btn v-on="on" small icon color="primary"
							v-visible="item.reportable"
							@click="launch_alignment_report(item.cram_job_id)"
						><k-icon small>fas fa-list-radio</k-icon></k-btn>
					</template>Alignment Report</v-tooltip>

				</td>
			</tr>
		</template>

	</k-data-table>

	<div class="d-flex mt-3 justify-center align-center">
		<div class="ml-3" style="width:600px">
			<k-select dense outlined hide-details append-icon="fas fa-caret-down"
				multiple
				label="Optional Columns To Show"
				v-model="columns_to_show_arr" 
				:items="column_menu_items"
			></k-select>
		</div>

		<div class="ml-10"><b style="color:#555">*Counts:</b> Resources × Competencies = “Cells” (total alignment comparisons)</div>
	</div>

	</k-card-text>
</k-card>
</k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import SCRAMReportMixin from './SCRAMReportMixin.js'

export default {
	mixins: [SCRAMReportMixin],
	components: {  },
	props: {
		scram_manager_update_id: { required: true },
		resource_framework_id_param: { required: false, default() { return ''} },
	},
	data() { return {
		dialog_open: true,
		initialized: false,

		jobs: [],
		footer_options: {
			itemsPerPageOptions: [25,50,100],
		},
		sort_by_column: 'cram_job_id',
		sort_descending: true,	
		search_terms: '',

		job_type_menu_items: [
			{ value: 'align', text: 'Resource -> Comp. ALIGNMENTS' },
			{ value: 'crosswalk', text: 'Comp. -> Comp. CROSSWALKS' },
		],

		column_menu_items: [
			{ value: 'creator_email', text: "Creator", default_show: false },
			{ value: 'resource_framework_title', text: "Resource Framework", default_show: true },
			{ value: 'comp_framework_title', text: "Competency Framework(s)", default_show: false },
			{ value: 'ai_stats', text: "Gen AI Stats", default_show: false },
		],
	}},
	computed: {
		...mapState(['site_config', 'framework_records', 'user_info']),
		...mapGetters([]),

		report_title() {
			if (empty(this.resource_framework_id)) {
				return `<b>Rosetta Align</b>`
			}
			// console.warn(this.rfr)
			let s = `<b>Rosetta Align:</b> `
			if (this.rfr) {
				s += `${this.rfr.json.CFDocument.title}`
			} else {
				s += `${this.resource_framework_id}`
			}
			return s
		},

		job_type_to_show: {
			get() { return this.$store.state.lst.scram_manager_job_type_to_show },
			set(val) { this.$store.commit('lst_set', ['scram_manager_job_type_to_show', val]) }
		},

		columns_to_show: {
			get() { 
				let store_obj = structuredClone(this.$store.state.lst.scram_manager_columns_to_show)
				let obj = {}
				for (let cmi of this.column_menu_items) {
					let key = cmi.value
					if (store_obj[key] === undefined) {
						obj[key] = cmi.default_show
					} else {
						obj[key] = store_obj[key]
					}
				}
				return obj
			},
			set(val) { this.$store.commit('lst_set', ['scram_manager_columns_to_show', val]) }
		},

		columns_to_show_arr: {
			get() {
				let arr = []
				for (let key in this.columns_to_show) {
					if (this.columns_to_show[key]) {
						arr.push(key)
					}
				}
				return arr
			},
			set(arr) {
				let o = {}
				for (let key in this.columns_to_show) {
					o[key] = (arr.includes(key))
				}
				this.columns_to_show = o
			}
		},

		table_headers() {
			let arr = []
			arr.push({ text: 'ID', align:'center', sortable: true, value: 'cram_job_id' } )
			arr.push({ text: 'Title', align:'left', sortable: true, value: 'cram_job_title' } )

			if (this.columns_to_show.creator_email) {
				arr.push({ text: 'Creator', align:'center', sortable: true, value: 'creator_email' } )
			}

			if (this.columns_to_show.resource_framework_title) {
				arr.push({ text: 'Resource Framework', align:'left', sortable: true, value: 'resource_framework_title' } )
			}

			if (this.columns_to_show.comp_framework_title) {
				arr.push({ text: 'Competency Framework(s)', align:'left', sortable: true, value: 'comp_framework_title' } )
			}

			arr.push({ text: 'Counts*', align:'center', sortable: true, value: 'matrixCellCount' } )
			arr.push({ text: 'Job Status', align:'center', sortable: true, value: 'worker_status_string' } )

			if (this.columns_to_show.ai_stats) {
				arr.push({ text: 'Gen AI Stats', align:'center', sortable: true, value: 'token_cost' } )
			}

			arr.push({ text: 'Actions', align:'center', sortable: false, value: 'cram_job_id' } )

			return arr
		},

		table_rows() {
			let arr = []
			for (let job of this.jobs) {
				// filter type we're not showing
				if (job.job_type != this.job_type_to_show) {
					continue
				}

				let jo = {}
				
				// get resource framework if specified
				jo.resource_framework_title = '—'
				if (!empty(job.universe_params?.resourceUniverse)) {
					let ru = JSON.parse(job.universe_params.resourceUniverse)
					let resource_fid = ru[0]?.frameworkIdentifier
					
					// if we're viewing jobs for a particular resource_framework_id, skip jobs not for this framework
					if (!empty(this.resource_framework_id)) {
						if (resource_fid != this.resource_framework_id) {
							continue
						}
					}
							
					let rfr = this.framework_records.find(x=>x.lsdoc_identifier == resource_fid)
					if (!empty(rfr)) {
						jo.resource_framework_title = rfr.json.CFDocument.title
					} else {
						jo.resource_framework_title = resource_fid
					}
				}

				jo.job = job
				jo.cram_job_id = job.cram_job_id
				jo.job_type_abbrev = (job.job_type == 'align') ? 'A' : 'C'
				jo.cram_job_title = job.cram_job_title ?? ''
				jo.creator_email = job.creator_email
				jo.worker_status = job.worker_status
				jo.token_cost = job.token_cost
				jo.resourceCount = 0
				jo.competencyCount = 0
				jo.matrixCellCount = 0

				// get comp framework if specified
				jo.comp_framework_title = '—'
				if (!empty(job.universe_params?.competencyUniverse)) {
					let cu = JSON.parse(job.universe_params.competencyUniverse)
					let comp_fid = cu[0]?.frameworkIdentifier
					let cfr = this.framework_records.find(x=>x.lsdoc_identifier == comp_fid)
					if (!empty(cfr)) {
						jo.comp_framework_title = cfr.json.CFDocument.title
					} else {
						jo.comp_framework_title = comp_fid
					}

					// list the first cu framework; then say " + X more" if there are more than 1
					if (cu.length > 1) {
						jo.comp_framework_title += ` + ${cu.length-1} more`
					}
				}

				// compose string for job status
				let missing_cells_count = (job.matrix_stats?.missingGenAICount ?? 0) + (job.matrix_stats?.missingCrosswalkCount ?? 0)
				if (missing_cells_count > 0 || ['incomplete','queued','processing','failed','cancelled'].includes(job.worker_status)) {
					if (job.worker_status == 'graceful_exit') {
						jo.worker_status_string = 'INCOMPLETE'
					} else {
						jo.worker_status_string = job.worker_status.toUpperCase()
					}

					// add missing count to worker_stats if appropriate
					if (missing_cells_count > 0) {
						jo.worker_status += ` (${missing_cells_count})`
					}

					jo.reportable = false

				} else {
					jo.worker_status_string = '<i class="fas fa-circle-check" style="font-size:16px; color:#444; margin-top:2px;"></i>'
					jo.reportable = true
				}

				// compose string for cram_job_title if we don't have one
				if (empty(jo.cram_job_title)) {
					jo.cram_job_title = '—'
				}

				// compose string for counts
				let counts_string = '—'
				if (!empty(job.matrix_stats)) {
					jo.resourceCount = job.matrix_stats.resourceCount
					jo.competencyCount = job.matrix_stats.competencyCount
					jo.matrixCellCount = job.matrix_stats.matrixCellCount
					counts_string = 
						`${job.matrix_stats.resourceCount} `
					  + `× ${job.matrix_stats.competencyCount} `
					  + `= ${job.matrix_stats.matrixCellCount}`
				}
				jo.counts_string = counts_string

				// compose string for locked date; matrix_locked_at is also the sort key for the column
				jo.matrix_locked_at = job.matrix_locked_at ?? ''
				jo.locked_string = '—'
				if (!empty(jo.matrix_locked_at)) {
					jo.locked_string = date.format(U.convert_to_local_date(jo.matrix_locked_at), 'M/D/YY h:mm A')
				}

				// compose string for ai stats
				let ai_stats = '—'
				if (!empty(job.token_cost)) {
					let seconds = job.genai_time_spent / 1000
					ai_stats = `$${job.token_cost.toFixed(2)}—${U.time_string(seconds)}`
					let genai_comparison_count = job.genai_comparison_count ?? job.matrix_stats?.matrixCellCount
					if (genai_comparison_count > 0) {
						let cell_cents = (job.token_cost / genai_comparison_count) * 100
						let cell_seconds = seconds / genai_comparison_count
						ai_stats += ` [${cell_cents.toFixed(3)}¢—${cell_seconds.toFixed(2)}s]`
					}
				}
				jo.ai_stats = ai_stats

				// if we have search terms, limit by them
				if (!empty(this.search_terms)) {
					let st = this.search_terms.toLowerCase()
					let found = false
					if (jo.cram_job_title.toLowerCase().includes(st)) found = true
					else if (jo.resource_framework_title.toLowerCase().includes(st)) found = true
					else if (jo.comp_framework_title.toLowerCase().includes(st)) found = true
					else if (jo.worker_status.toLowerCase().includes(st)) found = true

					// skip if search_terms not found in any of the above fields
					if (!found) continue
				}

				arr.push(jo)
			}
			return arr
		},

	},
	watch: {
		// params: { deep:true, immediate: true, handler() {
		// 	// console.warn('PARAMS WATCHER')
		// }},
		
		scram_manager_update_id() {
			// console.warn('scram_manager_update_id updated: ' + this.scram_manager_update_id)
			// this allows SCRAMJobView to trigger an update
			this.get_scram_jobs()
		},

	},
	created() {
	},
	async mounted() {
		vapp.scram_manager_component = this
		
		this.resource_framework_id = this.resource_framework_id_param
		if (!empty(this.resource_framework_id)) {
			this.rfr = await U.load_framework_and_cfo(this.resource_framework_id)
		}

		await this.get_scram_jobs()

		this.initialized = true
	},
	beforeDestroy() {
	},
	methods: {
		async get_scram_jobs() {
			let payload = {
				user_id: this.user_info.user_id,
				action: 'get_jobs',
			}

			U.loading_start()
			let result = await U.ajax_promise('align/scram', payload)
			U.loading_stop()

			if (result.status != 'ok') {
				this.$alert('ERROR: ' + result.status)
				return
			}

			this.jobs = result.jobs
		},

		edit_job(cram_job_id) {
			let params = {
				opened_from_embedded: false,
				cram_job_id: cram_job_id,
				initial_job_type_for_new_job: this.job_type_to_show,
			}
			if (!empty(this.resource_framework_id)) {
				params.initial_resource_framework_id = this.resource_framework_id
			}
			vapp.show_scram_job_view(params)
		},

		view_grid(cram_job_id) {
			vapp.show_resource_matrix_view({
				cram_job_id: cram_job_id+'',
				dim: 'grid',
			})
		},

		launch_alignment_report(cram_job_id) {
			window.open(`/crm/${cram_job_id}/ar`)
		},

		create_new_job() {
			this.edit_job(0)
		},
	}
}
</script>

<style lang="scss">
.k-rcram-jobs-table {
	th {
		white-space:nowrap;
	}

	td {
		font-size:14px!important;
		line-height:17px!important;
		padding:4px!important;
	}

	.k-rcram-manager-id-cell {
		text-align:center;
	}

	.k-rcram-manager-job-title-cell {
		text-align:left;
	}

	.k-rcram-framework-title-cell {
		text-align:left;
	}

	.k-rcram-manager-creator-cell {
		text-align:center;
		font-size:12px!important;
	}

	.k-rcram-manager-count-cell {
		text-align:center;
		white-space:nowrap;
	}

	.k-rcram-manager-status-cell {
		text-align:center;
		white-space:nowrap;
		font-size:12px!important;
	}

	.k-rcram-manager-ai-cost-cell {
		text-align:center;
		white-space:nowrap;
		font-size:12px!important;
	}


	.k-rcram-manager-actions-cell {
		text-align:center;
		white-space:nowrap;
	}
}
</style>
